import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';

// Set the BASE_URL here
const BASE_URL = 'https://oxfordstudycenterapp.site';

const Assessments = () => {
  const [assessments, setAssessments] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  // Fetch assessments from the API (general or search based on search query)
  const fetchAssessments = async (page = 1, query = '') => {
    setLoading(true);
    try {
      let response;
      if (query) {
        // Fetch using search API
        response = await axios.get(`${BASE_URL}/assessments/search-assessment`, {
          params: {
            page,
            limit: 10,
            AssessmentName: query, // Search by name
          },
        });
      } else {
        // Fetch all assessments
        response = await axios.get(`${BASE_URL}/assessments/get-all-assessments`, {
          params: {
            page,
            limit: 10,
          },
        });
      }

      setAssessments(response.data.assessments || response.data); // Handle both formats
      setTotalPages(response.data.pagination?.totalPages || 1);
    } catch (error) {
      console.error('Error fetching assessments:', error);
      toast.error('Failed to load assessments.');
    } finally {
      setLoading(false);
    }
  };

  // Handle search input change
  const handleSearch = (e) => {
    setSearchQuery(e.target.value);
  };

  // Submit search form
  const handleSearchSubmit = (e) => {
    e.preventDefault();
    fetchAssessments(1, searchQuery); // Fetch with search term and reset page to 1
  };

  // Reset search to get all assessments
  const handleResetSearch = () => {
    setSearchQuery(''); // Clear search query
    fetchAssessments(1); // Reset to the first page and fetch all assessments
  };

  // Pagination control
  const handlePageChange = (newPage) => {
    setCurrentPage(newPage);
    fetchAssessments(newPage, searchQuery); // Fetch data for new page
  };

  // Function to download the assessment document
  const handleDownloadDocument = (documentFileName) => {
    if (documentFileName) {
      // Use the BASE_URL for constructing the download URL
      const downloadUrl = `${BASE_URL}/assessments/download-document/${documentFileName}`;
      window.open(downloadUrl, '_blank'); // Open document URL in a new tab for download
    } else {
      toast.error('No document available for download.');
    }
  };

  // Load initial data
  useEffect(() => {
    fetchAssessments(currentPage); // Fetch all assessments initially
  }, [currentPage]);

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-sm p-6">
          <div className="mb-6">
            <h1 className="text-2xl font-bold text-gray-800">Assessments</h1>
            <div className="h-1 w-full bg-gray-200 mt-2"></div>
          </div>

          {/* Search Form */}
          <form onSubmit={handleSearchSubmit} className="mb-6 flex gap-4">
            <div className="flex-1">
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearch}
                placeholder="Search by Assessment Name"
                className="w-full px-4 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-pink-500 focus:border-pink-500 transition-colors"
              />
            </div>
            <button
              type="submit"
              className="bg-pink-600 text-white px-6 py-2 rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors"
            >
              Search
            </button>
            <button
              type="button"
              onClick={handleResetSearch}
              className="bg-gray-100 text-gray-700 px-6 py-2 rounded-md hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-offset-2 transition-colors"
            >
              Reset
            </button>
          </form>

          {/* Loading Indicator */}
          {loading && (
            <div className="flex justify-center items-center py-12">
              <div className="relative">
                <div className="w-12 h-12 rounded-full absolute border-4 border-pink-200"></div>
                <div className="w-12 h-12 rounded-full animate-spin absolute border-4 border-pink-600 border-t-transparent"></div>
              </div>
            </div>
          )}

          {/* No Assessments Found */}
          {!loading && assessments.length === 0 && (
            <div className="text-center py-12">
              <p className="text-gray-500 text-lg">No assessments found.</p>
            </div>
          )}

          {/* Assessments List */}
          {!loading && assessments.length > 0 && (
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead className="bg-pink-600">
                  <tr>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Assessment Name</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Date Created</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Due Date</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Class</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Teacher</th>
                    <th className="px-6 py-3 text-left text-xs font-medium text-white uppercase tracking-wider">Actions</th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-200">
                  {assessments.map((assessment) => (
                    <tr key={assessment.id} className="hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">{assessment.AssessmentName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(assessment.DateCreated).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(assessment.DateDue).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">{assessment.ClassName}</td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {`${assessment.EmployeeName} ${assessment.EmployeeSurname}`}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium space-x-2">
                        <button
                          onClick={() => handleDownloadDocument(assessment.Document)}
                          className="text-pink-600 hover:text-pink-900 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 rounded-md px-3 py-1"
                        >
                          Download
                        </button>
                        <Link
                          to={`/assessment-results/${assessment.id}`}
                          className="text-green-600 hover:text-green-900 focus:outline-none focus:ring-2 focus:ring-green-500 focus:ring-offset-2 rounded-md px-3 py-1"
                        >
                          Results
                        </Link>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {/* Pagination Controls */}
          {!loading && totalPages > 1 && (
            <div className="flex justify-center items-center mt-6 space-x-4">
              <button
                onClick={() => handlePageChange(currentPage - 1)}
                disabled={currentPage === 1}
                className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Previous
              </button>
              <span className="text-sm text-gray-600">
                Page {currentPage} of {totalPages}
              </span>
              <button
                onClick={() => handlePageChange(currentPage + 1)}
                disabled={currentPage === totalPages}
                className="px-4 py-2 bg-pink-600 text-white rounded-md hover:bg-pink-700 focus:outline-none focus:ring-2 focus:ring-pink-500 focus:ring-offset-2 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Next
              </button>
            </div>
          )}
        </div>
      </div>

      <ToastContainer />
    </div>
  );
};

export default Assessments;
